import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Navbar from "@/components/layout/Navbar";

/**
 * Reset-password page. Consumes the `token` query param from the email link,
 * takes a new password, and posts to /api/auth/reset-password. On success
 * redirects to /auth?mode=login so the user re-authenticates with the new pw.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("This link is missing or invalid.");
    }
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters.");
    if (pw !== pw2) return toast.error("Passwords don't match.");
    if (!token) return toast.error("Missing reset token.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated. Please sign in.");
      navigate("/auth?mode=login");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password. Request a new link.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="container mx-auto px-4 pt-32 pb-16">
        <div className="max-w-md mx-auto">
          <div className="brut-card bg-white p-8">
            <div className="overline mb-2">Account recovery</div>
            <h1 className="font-display font-black text-3xl mb-6">Choose a new password</h1>

            {!token ? (
              <div className="space-y-4" data-testid="reset-missing-token">
                <p className="text-sm font-mono text-red-700">
                  This reset link is missing its token. Please request a new one.
                </p>
                <Link to="/forgot-password" className="brut-btn-primary w-full justify-center">
                  Request new link
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <label className="overline block mb-2">New password</label>
                  <input
                    type="password"
                    className="brut-input"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    required
                    minLength={8}
                    data-testid="reset-input-pw"
                    autoComplete="new-password"
                  />
                  <p className="text-xs font-mono text-muted-foreground mt-2">At least 8 characters.</p>
                </div>
                <div>
                  <label className="overline block mb-2">Confirm new password</label>
                  <input
                    type="password"
                    className="brut-input"
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    required
                    minLength={8}
                    data-testid="reset-input-pw2"
                    autoComplete="new-password"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="brut-btn-primary w-full justify-center"
                  data-testid="reset-submit"
                >
                  {loading ? "Updating…" : "Update password"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
